import './ModalMenu.scss';

import * as React from 'react';

import cx from 'classnames';

import { FocusIndicator } from '~shared/components/FocusIndicator';
import { IconChevron } from '~shared/components/icons/standard';
import { Tab, TabProps, TabsProvider } from '~shared/components/Tabs';

import { useModalMenu } from './';

interface Props {
  children: React.ReactNode;
}

export function ModalMenu({ children }: Props) {
  const [showSidebar, setShowSidebar] = useModalMenu();

  const modalMenuClass = cx(
    'grid grid-cols-1 w-full h-full p-0 overflow-hidden',
    'modal-menu',
    {
      'is-sidebar-visible': showSidebar,
    }
  );

  return (
    <TabsProvider id="modalMenu" onChange={() => setShowSidebar(false)}>
      <div className={modalMenuClass}>{children}</div>
    </TabsProvider>
  );
}

export function ModalMenuSidebar({ children }: Props) {
  return (
    <div className="z-10 col-start-1 row-start-1 md:bg-grey-50 transition-transform ease-out duration-200 modal-menu__sidebar">
      {children}
    </div>
  );
}

interface MenuTabProps extends Omit<TabProps, 'children'> {
  children: React.ReactNode;
}

/**
 * This is the same as the `Tab` component for all intents and
 * purposes, modified to include a chevron icon with the label
 * text, as well as custom styling, at mobile sizes.
 */
export function ModalMenuTab({ children, ...props }: MenuTabProps) {
  const getButtonClass = (isSelected, isDisabled) =>
    cx(
      'group relative z-10 flex justify-between items-center m-0 px-8 py-5 bg-transparent border-0 appearance-none outline-none select-none',
      'tabs-tab',
      {
        'md:bg-grey-200': isSelected,
        'text-grey-800 cursor-default': isDisabled,
        'text-blue-900 cursor-pointer': !isDisabled,
      }
    );

  return (
    <Tab {...props}>
      {({ isActive, ...buttonProps }) => (
        <button
          {...buttonProps}
          className={getButtonClass(isActive, buttonProps.disabled)}
        >
          <FocusIndicator notOffset isInset />
          <div className="w-full font-display font-500 text-display leading-8 antialiased text-left">
            {children}
          </div>
          <div
            className={`absolute right-0 hidden md:flex w-1 h-full bg-cyan-700 ${
              !isActive && 'opacity-0'
            }`}
          />
          <div className="flex md:hidden text-cyan-600">
            <IconChevron direction="right" />
          </div>
        </button>
      )}
    </Tab>
  );
}

export function ModalMenuContent({ children }: Props) {
  return <div className="md:rounded-r-6 modal-menu__content">{children}</div>;
}
